import {useEffect, useMemo, useState} from "react";
import {MODEL_HEADER} from "../Model/Model";
import {GetAllZone} from "../../../../../reduxToolkit/EmployeeOnBoarding/EmployeeOnBoardingApi";
import {getWorkStations} from "../../../../../reduxToolkit/CompanyEmployees/CompanyEmployeesApi";
import {useDispatch} from "react-redux";
import HandleChange from "../../../../../Helpers/HandleChange";
import {useTranslation} from "react-i18next";
import {UseWebSocket} from "./useWebSocket";
import {GetUserRestriction} from "../../../../../reduxToolkit/restrictions/RestrictionApi";
import {useDailyTrigger} from '../../../../../hooks/useDailyTrigger';

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 * Custom hook to manage incoming visitor invitations in different workflow states.
 *
 * Responsibilities:
 * - Fetching zones, workstations, and a sample invitation.
 * - Managing separate invitation lists by status:
 *    - pendingToComplete
 *    - pendingValidation
 *    - readyToEnter
 *    - checkIn
 * - Handling search/filtering across all invitation lists.
 * - Managing WebSocket reconnections and online/offline state.
 * - Providing a reactive model object for form handling via `handleChange`.
 *
 */


export const VisitorIncomingInvitationApp = ()=> {
    const { t } = useTranslation();
    const [isOnline, setIsOnline] = useState(false);
    const [isReload, setIsReload] = useState(0);
    // states for every column
    const [pendingToComplete, setPendingToComplete] = useState([]);
    const [pendingValidation, setPendingValidation] = useState([]);
    const [readyToEnter, setReadyToEnter] = useState([]);
    const [checkIn, setCheckIn] = useState([]);
    const [item, setItem] = useState({...MODEL_HEADER});
    const [search, setSearch] = useState('');
    const dispatch = useDispatch();
    const { handleChange } = HandleChange({ model: item, setModel: setItem });


    useEffect(()=>{
        dispatch(GetAllZone());
        dispatch(getWorkStations());
        dispatch(GetUserRestriction());
    },[dispatch]);

    useDailyTrigger({setItem});

    const { reconnectWebSocket } = UseWebSocket(
        {
            item,
            isOnline,
            setIsOnline,
            setPendingToComplete,
            setPendingValidation,
            setReadyToEnter,
            setCheckIn
        }
    );

    /**
     * Filters a list of invitations based on a search string.
     *
     * @param {Array} list - Array of invitation objects to filter.
     * @param {string} search - The search term to filter by.
     * @returns {Array} Filtered list containing invitations where any of the
     *                  following fields include the search term:
     *                  - host name, host last name, host second last name
     *                  - visitor name, visitor last name, visitor second last name
     *                  - visitor type name
     *                  - zone name
     *                  - root zone name
     */
    const filterBySearch = (list, search) => {
        if (!search) return list;

        const lowerSearch = search.toLowerCase();

        return list.filter(order => {
            const host = `${order?.hostName || ''} ${order?.hostLastName || ''} ${order?.hostSecondLastName || ''}`.toLowerCase();
            const visitor = `${order?.visitorName || ''} ${order?.visitorLastName || ''} ${order?.visitorSecondLastName || ''}`.toLowerCase();
            const visitorType = (order?.visitorType?.name || '').toLowerCase();
            const zone = (order?.zoneName || '').toLowerCase();
            const rootZone = (order?.rootFatherZoneName || '').toLowerCase();

            return (
                host.includes(lowerSearch) ||
                visitor.includes(lowerSearch) ||
                visitorType.includes(lowerSearch) ||
                zone.includes(lowerSearch) ||
                rootZone.includes(lowerSearch)
            );
        });
    };

    /**
     * Memoized filtered lists based on current search value.
     * Prevents unnecessary recalculation when the search or the source list hasn't changed.
     */
    const withSearchPendingToComplete = useMemo(
        () => filterBySearch(pendingToComplete, search),
        [search, pendingToComplete]
    );

    const withSearchPendingValidation = useMemo(
        () => filterBySearch(pendingValidation, search),
        [search, pendingValidation]
    );

    const withSearchReadyToEnter = useMemo(
        () => filterBySearch(readyToEnter, search),
        [search, readyToEnter]
    );

    const withSearchCheckIn = useMemo(
        () => filterBySearch(checkIn, search),
        [search, checkIn]
    );


    /**
     * Set the value of setIsReload
     */
    useEffect(() => {
        if(isOnline){
            setIsReload(0);
        }
    }, [isOnline]);


    useEffect(() => {
        if(isReload && isReload > 0 ){
            reconnectWebSocket();
        }
    }, [isReload]);

    return {
        isOnline,
        setIsReload,
        item,
        handleChange,
        t,
        withSearchPendingToComplete,
        withSearchPendingValidation,
        withSearchReadyToEnter,
        withSearchCheckIn,
        search,
        setSearch
    };
};
